/**
 * Privacy policy for PostHog, kept as pure functions so it can be tested
 * without a browser or a live PostHog connection.
 *
 * Agreed with Jose after the BAA was signed:
 *
 *   Homepage, condition pages, blog   sensitive URLs grouped
 *   Sign up                           all inputs masked
 *   Questionnaire                     inputs and on-screen text masked
 *   Checkout                          everything masked, card fields blank
 *
 * Two things shape the implementation:
 *
 * 1. rrweb reads its masking options once, when recording starts. Rather than
 *    reconfiguring on every route change (which is not a supported operation
 *    and would silently do nothing), the strictest *input* masking is set
 *    globally and text masking is decided per text node by maskTextFn, which
 *    rrweb calls at record time. That makes the policy route-aware without
 *    ever restarting the recorder.
 *
 * 2. Masking only affects replay. Event properties are separate, so the URL a
 *    patient is on would still be sent in full. On this site the URL is the
 *    sensitive part: /sexual-diseases/doxypep identifies a health interest on
 *    its own. groupUrl() collapses the specific condition out of the path
 *    before anything leaves the browser.
 */

/** Areas of the site, in order of how much they need protecting. */
export const AREA = {
  PUBLIC: 'public',
  SIGNUP: 'signup',
  QUESTIONNAIRE: 'questionnaire',
  CHECKOUT: 'checkout',
};

// Checked before the questionnaire prefixes, because /diagnosis/checkout sits
// underneath /diagnosis and must not be treated as a questionnaire page.
const CHECKOUT_PATHS = [
  '/diagnosis/checkout',
  '/diagnosis/mobile/checkout',
  '/diagnosis/abandonedCart',
  '/subscribe/checkout',
  '/payment-confirmation',
];

const QUESTIONNAIRE_SEGMENTS = [
  'medical_info',
  'treatment_options',
  'pharmacy_info',
  'product_recommendation',
];

const SIGNUP_PATHS = ['/login', '/signup', '/subscribe/subscription'];

/** Which policy applies to a given pathname. */
export function areaFor(pathname) {
  const path = String(pathname || '').toLowerCase();

  if (CHECKOUT_PATHS.some((p) => path.startsWith(p.toLowerCase()))) {
    return AREA.CHECKOUT;
  }
  if (QUESTIONNAIRE_SEGMENTS.some((seg) => path.includes(seg))) {
    return AREA.QUESTIONNAIRE;
  }
  if (SIGNUP_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
    return AREA.SIGNUP;
  }
  // The diagnosis flow's own sign-up steps live at /diagnosis itself.
  if (path === '/diagnosis' || path.startsWith('/diagnosis')) {
    return AREA.SIGNUP;
  }
  // The patient's own account area is never recorded at all (see shouldRecord).
  if (path.startsWith('/dashboard')) return AREA.CHECKOUT;

  return AREA.PUBLIC;
}

/** On-screen text is masked in the questionnaire and at checkout. */
export function shouldMaskText(pathname) {
  const area = areaFor(pathname);
  if (area === AREA.QUESTIONNAIRE || area === AREA.CHECKOUT) return true;
  // Sexual health and lab testing, including the landing pages, which are a
  // single segment and so are not caught by grouping alone. This is the gap
  // Jose spotted on /lab-testing.
  return isSensitivePath(pathname);
}


/**
 * Services whose own name is the disclosure.
 *
 * The sign-up events carry the service being signed up for, which is what
 * makes per-condition drop-off readable — and for a sexual health service that
 * name alone is the sensitive fact, exactly what grouping the URL was meant to
 * stop. The sign-up flow lives at /diagnosis, so the path says nothing about
 * which condition it is; the only thing available is the service's category.
 *
 * Matched by Services_Category objectId because the pointer stored in
 * localStorage.diseaseData is unexpanded and carries no slug. Ids read from
 * the live Services_Category class:
 *
 *   YAhR6Nfm4U  sexual-diseases
 *   LN5pjN3x6p  std-and-urinalysis-tests-self-pay
 *
 * If a category is ever added or re-created these need updating, so the
 * slugs are recorded alongside the ids.
 */
export const SENSITIVE_CATEGORY_IDS = ['YAhR6Nfm4U', 'LN5pjN3x6p'];

export function isSensitiveServiceCategory(categoryId) {
  return SENSITIVE_CATEGORY_IDS.includes(String(categoryId || ''));
}

/**
 * Human-readable attributes carry the same disclosure as on-screen text, and
 * masking text nodes does not touch them. The one that actually leaked was the
 * <meta name="description"> in <head>, which named three STD panels in full on
 * /lab-testing while every visible string on the page was masked:
 *
 *   "...order STD lab tests online. Select tests like chlamydia, gonorrhea,
 *    or trichomoniasis, pay securely, and visit a nearby lab."
 *
 * It is never rendered, so nobody watching the replay would see it, but it is
 * still sitting in the stored payload. Same shape as the document title leak,
 * one layer further down.
 */
const MASKED_ATTRIBUTES = [
  'content', // meta description, og:*, twitter:*
  'alt',
  'title',
  'aria-label',
  'aria-description',
  'placeholder',
];

// Grouped rather than masked, so replay still resolves assets and the link
// structure stays readable.
const URL_ATTRIBUTES = ['href', 'src'];

/**
 * Decides what an attribute looks like in the replay. Applies on exactly the
 * pages that mask text, so the two rules cannot drift apart.
 *
 * @param {string} name     Attribute name.
 * @param {string} value    Its value.
 * @param {string} pathname The page being recorded.
 */
export function maskAttribute(name, value, pathname) {
  if (!value || typeof value !== 'string') return value;
  const key = String(name).toLowerCase();

  // A condition slug in a link discloses as much as the page URL does, so it
  // goes through the same grouping, on every page rather than only the
  // sensitive ones.
  if (URL_ATTRIBUTES.includes(key)) return groupUrl(value);

  if (!shouldMaskText(pathname)) return value;
  if (MASKED_ATTRIBUTES.includes(key)) {
    return value.replace(/\S/g, '*');
  }
  return value;
}

/**
 * Autocapture is off everywhere.
 *
 * It was originally allowed on the public pages, on the grounds that the
 * design work happens there and they carry no PHI. That was wrong. Autocapture
 * records the TEXT of whatever was clicked, and on this site that text is the
 * condition itself: live data showed `clicked button with text "Chlamydia"`
 * on /sexual-diseases. Grouping the URL to /sexual-diseases/:condition
 * achieves nothing if the very next event names the condition anyway.
 */
export function shouldAutocapture() {
  return false;
}

/**
 * True when groupPath() would rewrite this path, i.e. the URL itself was
 * considered identifying. If it is worth hiding in the URL it is worth hiding
 * on screen, so these pages get text masking in replay too. Without this a
 * recording of /sexual-diseases/:condition still shows the condition name in
 * the heading.
 */
export function isSensitivePath(pathname) {
  const head = String(pathname || '/')
    .split('/')
    .filter(Boolean)[0];
  return SENSITIVE_PREFIXES.includes(String(head || '').toLowerCase());
}


/** The patient dashboard is not recorded at all. */
export function shouldRecord(pathname) {
  return !String(pathname || '').toLowerCase().startsWith('/dashboard');
}

/**
 * Collapses identifying slugs out of a path so the URL cannot reveal which
 * condition, medication or article a visitor was looking at.
 *
 * /sexual-diseases/doxypep        -> /sexual-diseases/:condition
 * /urgent-care/dental-infection   -> /urgent-care/:condition
 * /medications/sildenafil         -> /medications/:medication
 * /blog/how-to-treat-a-uti        -> /blog/:post
 * /services/8KyRM9wX5j/dental...  -> /services/:id/:condition
 *
 * Category level is kept, so drop-off by category is still measurable.
 */
// Agreed with Jose on 20 Oct: exact URLs for ordinary conditions, sexual
// health grouped. Lab tests sit here too because the STD panels reveal the
// same thing, and /lab-testing is the landing page for them.
const SENSITIVE_PREFIXES = [
  'sexual-diseases',
  'std-and-urinalysis-tests-self-pay',
  'lab-testing',
];

export function groupPath(pathname) {
  const path = String(pathname || '/');
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return '/';

  const head = parts[0].toLowerCase();

  // Blog grouped at Jose's request: he does not need per-article analytics
  // yet. Revisit if the blog becomes a focus.
  if (head === 'blog' && parts.length > 1) return '/blog/:post';

  // Sexual health and the STD lab panels: the slug is the whole disclosure.
  if (SENSITIVE_PREFIXES.includes(head) && parts.length > 1) {
    return `/${head}/:condition`;
  }

  // Ordinary conditions and medications are sent exactly as they are, so the
  // per-condition numbers are usable.
  return '/' + parts.join('/');
}


/**
 * Rewrites a full URL, dropping the query string entirely. Campaign
 * parameters are the only thing we would want from it, and PostHog has
 * already read those into its own properties by the time this runs.
 */
export function groupUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    return `${u.origin}${groupPath(u.pathname)}`;
  } catch {
    return groupPath(url);
  }
}

/**
 * True when a string is one of our URLs or a site path, and so has to be
 * grouped before it leaves.
 */
function looksLikeUrlOrPath(value) {
  return /^https?:\/\//i.test(value) || value.startsWith('/');
}

/**
 * Rewrites every URL or path anywhere in an event's properties, however deeply
 * nested, rather than naming the properties to fix.
 *
 * This replaced a list of six property names, which is how a live export came
 * to contain the slug in full on eleven separate properties. The SDK sends far
 * more URL-bearing properties than the obvious ones, several of them nested,
 * and it adds new ones between versions:
 *
 *   $session_entry_url          /diagnosis?diseaseID=ZcxGw19kQc
 *   $session_entry_referrer     /sexual-diseases/chlamydia
 *   $prev_pageview_pathname     /sexual-diseases/chlamydia
 *   $set_once.$initial_referrer /sexual-diseases/chlamydia
 *   $web_vitals_LCP_event.navigationURL, and more
 *
 * Naming them one at a time loses that race every time the SDK is upgraded, so
 * anything shaped like a URL is grouped wherever it appears. Values that are
 * not URLs, such as an element selector or "$direct", are left alone.
 */
function sanitiseUrlsDeep(value, depth) {
  if (depth > 8) return value;
  if (typeof value === 'string') {
    return looksLikeUrlOrPath(value) ? groupUrl(value) : value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => sanitiseUrlsDeep(v, depth + 1));
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) {
      // Keys carry URLs too, and only values were being rewritten.
      // $heatmap_data is an object keyed by the page its points were collected
      // on, so a live capture on a condition page showed the whole raw URL,
      // diseaseID query string included, going out untouched on every heatmap
      // batch while every URL in a value beside it was grouped.
      const outKey = looksLikeUrlOrPath(key) ? groupUrl(key) : key;
      const sanitised = sanitiseUrlsDeep(value[key], depth + 1);
      out[outKey] = mergeGrouped(out[outKey], sanitised);
    }
    return out;
  }
  return value;
}

/**
 * Combines two values that ended up under the same key once their URLs were
 * grouped. Two pages that differ only by a stripped query string become one
 * key, and for heatmap points that is exactly right: they are the same page.
 */
function mergeGrouped(existing, incoming) {
  if (existing === undefined) return incoming;
  if (Array.isArray(existing) && Array.isArray(incoming)) {
    return existing.concat(incoming);
  }
  return incoming;
}

/**
 * Campaign parameters that name what the visitor was searching for.
 *
 * utm_term carries the Google keyword verbatim. Jose's example was
 * "online antibiotics for tooth infection", and on a sexual health ad that
 * keyword IS the condition, which is the one thing grouping the URL exists to
 * stop. It is saved against the person, so it outlives the session.
 *
 * utm_source, utm_medium and utm_campaign stay. They are what PostHog builds
 * channel type from and none of them names a condition. The ad click ids
 * (gclid and friends) are deliberately left alone too: Google can turn a
 * gclid back into the keyword, so they are a real exposure, but removing them
 * changes paid attribution and that is Jose's call to make, not ours.
 */
const SEARCH_TERM_KEYS = [
  'utm_term',
  '$initial_utm_term',
  '$session_entry_utm_term',
];

/**
 * Properties that name a page the visitor was on earlier rather than the one
 * the event came from. They are how the account area kept leaking: a click
 * collected on /dashboard is flushed after the visitor has moved on, by which
 * time the live pathname is a public page and the area check below has already
 * let the event through.
 */
const CARRIED_PATH_KEYS = [
  '$session_entry_url',
  '$session_entry_pathname',
  '$prev_pageview_pathname',
  '$initial_current_url',
  '$initial_pathname',
];

/**
 * Strips what must never leave from one bag of properties.
 *
 * Used for the event's own properties and, just as importantly, for the
 * top-level $set and $set_once bags. Those sit beside `properties` on the
 * event rather than inside it, so everything below used to miss them
 * completely and $set_once.$initial_current_url went out as the raw landing
 * URL, query string and all.
 */
function scrubBag(bag) {
  if (!bag || typeof bag !== 'object') return bag;
  const out = sanitiseUrlsDeep(bag, 0);

  for (const key of SEARCH_TERM_KEYS) delete out[key];

  // Element text is how the condition leaked in the first place.
  delete out.$el_text;
  delete out.$elements;
  delete out.$elements_chain;
  // Query params can carry a diseaseID or an email from a reminder link.
  delete out.$current_url_search;
  delete out.$search_engine;

  // The account area is never recorded, so a reference to it carried on an
  // event from somewhere else is dropped rather than grouped.
  for (const key of CARRIED_PATH_KEYS) {
    if (!shouldRecord(pathOf(out[key]))) delete out[key];
  }

  // Heatmap points are filed under the page they were collected on, so a
  // bucket keyed to the account area is dashboard data on an event that left
  // from elsewhere. Drop the bucket, keep the rest.
  if (out.$heatmap_data && typeof out.$heatmap_data === 'object') {
    for (const key of Object.keys(out.$heatmap_data)) {
      if (!shouldRecord(pathOf(key))) delete out.$heatmap_data[key];
    }
    if (Object.keys(out.$heatmap_data).length === 0) delete out.$heatmap_data;
  }

  return out;
}

/** The pathname of a value that may be a full URL, a path, or neither. */
function pathOf(value) {
  if (typeof value !== 'string' || !value) return '/';
  try {
    return new URL(value).pathname;
  } catch {
    return value.startsWith('/') ? value : '/';
  }
}

/**
 * Applied to every outgoing event. Returns null to drop the event entirely.
 */
export function sanitiseEvent(event, pathname) {
  if (!event) return event;

  // The patient's own account area sends nothing whatsoever. shouldRecord()
  // only ever stopped the session recorder here, so pageviews, web vitals and
  // heatmaps were still going out from /dashboard. Dropping the event is the
  // only thing that actually makes the area silent.
  if (!shouldRecord(pathname)) return null;

  // Autocapture carries the clicked element's text, which replay masking does
  // not cover. Drop it outside the public pages.
  // Belt and braces: these are disabled at init, but remote config can turn
  // them back on from the PostHog dashboard without a deploy, so they are
  // filtered here too.
  const ELEMENT_EVENTS = ['$autocapture', '$rageclick', '$dead_click', '$exception'];
  if (ELEMENT_EVENTS.includes(event.event)) {
    return null;
  }

  const props = scrubBag(event.properties || {});

  // $set and $set_once sit beside `properties` on the event, not inside it,
  // so they bypassed every rule above until now. They are also the bags that
  // persist: whatever lands there is stored against the person rather than
  // against one event, which is exactly how the raw landing URL and the
  // search keyword were outliving the session.
  for (const bag of ['$set', '$set_once']) {
    if (event[bag] && typeof event[bag] === 'object') {
      event[bag] = scrubBag(event[bag]);
    }
  }

  // PostHog attaches the document title to every pageview, and on this site
  // the SEO title names the condition outright:
  // "Antibiotics For Tooth Infection - Dental Infection Treatment".
  // Grouping the URL is pointless while that rides along beside it.
  if (shouldMaskText(pathname)) {
    delete props.title;
    delete props.$screen_name;
  }
  event.properties = props;

  return event;
}
